import { isNone } from '@ember/utils';
import { Data, TransformsList } from 'cio/utils/analytics/transforms';
import { IntegrationSource } from 'cio/utils/analytics/transforms/integrations';
import { MessagingChannelType } from 'cio/utils/setup-guide/messaging-channels';
import SetupGuide from 'cio/models/setup-guide';

export type SetupGuideDataType = 'person' | 'attribute' | 'event';
export type SetupGuideSkippableStep = 'link_tracking' | 'universal_link_tracking' | 'android' | 'ios';

function onboardingStepName(data: Data & { model: SetupGuide }) {
  if (!data?.model?.startedAt) {
    return 'Welcome';
  }

  if (!data.route) {
    return '';
  }

  let routeTitles: Record<string, string> = {
    'setup-guide.sources': 'Integrate your data',
    'setup-guide.channels.index': 'Set up messaging channels',
    'setup-guide.channels.email': 'Email',
    'setup-guide.channels.slack': 'Slack',
    'setup-guide.channels.twilio': 'Twilio',
    'setup-guide.channels.webhook': 'Webhook',
    'setup-guide.channels.push': 'Push',
    'setup-guide.channels.in_app': 'In-App',
    'setup-guide.send-campaign': 'Send your campaign',
    'setup-guide.create-campaign': 'Create your first campaign',
  };

  return routeTitles[data.route.name];
}

function isOnboardingStepCompleted(data: Data & { model: SetupGuide }): boolean {
  if (!data.route) {
    return false;
  }

  let routeCompleted: Record<string, boolean> = {
    'setup-guide.sources': data.model.completedSteps.sources,
    'setup-guide.channels.index': data.model.everyMessagingChannelComplete,
    'setup-guide.channels.email': !isNone(data.model.email?.completedAt),
    'setup-guide.channels.slack': !isNone(data.model.slack?.completedAt),
    'setup-guide.channels.twilio': !isNone(data.model.twilio?.completedAt),
    'setup-guide.channels.webhook': !isNone(data.model.webhook?.completedAt),
    'setup-guide.channels.push': !isNone(data.model.push?.completedAt),
    'setup-guide.channels.in_app': !isNone(data.model.in_app?.completedAt),
    'setup-guide.send-campaign': data.model.completedSteps['send-campaign'],
    'setup-guide.create-campaign': data.model.completedSteps['create-campaign'],
  };

  return routeCompleted[data.route.name];
}

const Transforms: TransformsList = {
  detailsClicked(data: Data & { type: IntegrationSource }) {
    return {
      'Integration Name': {
        js: 'Javascript',
        api: 'API',
        segment: 'Segment.com',
        csv: 'CSV Import',
        forms: 'Forms',
      }[data.type],
    };
  },

  dataTypeClicked(data: Data & { type: SetupGuideDataType }) {
    return {
      'Data Type': {
        person: 'People',
        event: 'Events',
        attribute: 'Attributes',
      }[data.type],
    };
  },

  setupChannelsClicked(data: Data & { types: MessagingChannelType[] }) {
    return {
      'Channel Count': data.types.length,
      Email: data.types.includes(MessagingChannelType.Email),
      Push: data.types.includes(MessagingChannelType.Push),
      Twilio: data.types.includes(MessagingChannelType.Twilio),
      Slack: data.types.includes(MessagingChannelType.Slack),
      Webhooks: data.types.includes(MessagingChannelType.Webhook),
      InApp: data.types.includes(MessagingChannelType.InApp),
    };
  },

  stepSkipped(data: Data & { step: SetupGuideSkippableStep }) {
    return {
      Step: {
        link_tracking: 'Link Tracking',
        universal_link_tracking: 'Universal Links',
        android: 'Android',
        ios: 'iOS',
      }[data.step],
    };
  },

  pageViewed(data: Data & { model: SetupGuide }) {
    return {
      'Page Name': onboardingStepName(data),
      Completed: isOnboardingStepCompleted(data),
    };
  },

  pricingCardButtonClick(data: Data & { type: string }) {
    return {
      'Button Name': {
        essentials: 'essentials ',
        premium: 'premium',
        enterprise: 'enterprise',
        startup: 'startup',
        compare_plans: 'compare plans',
      }[data.type],
    };
  },

  setupGuideProductTourVideoStarted(data) {
    return {
      'Account Id': data.account_id,
    };
  },

  setupGuideProductTourVideoLoaded(data) {
    return {
      'Account Id': data.account_id,
    };
  },
  setupGuideProductTourVideoNotIncluded(data) {
    return {
      'Account Id': data.account_id,
    };
  },
  upgradeClicked(data) {
    return {
      'Account Id': data.account_id,
    };
  },
  trackAccount(data) {
    return {
      'Account Id': data.account_id,
    };
  },
};

export default Transforms;
